<template>
  <v-card flat>
    <v-card-text>
      <v-container>
        <!-- Скелетон для информации об устройстве -->
        <v-row v-if="loadingDetails && !deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-information-outline</v-icon>
              {{ $t('traccar.device_info') }}
            </h3>
            <v-row>
              <v-col v-for="i in 6" :key="'skeleton-info-' + i" cols="6" sm="4" md="3" lg="2">
                <v-skeleton-loader type="list-item-two-line" class="mx-0"></v-skeleton-loader>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Информация об устройстве -->
        <v-row v-if="deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-information-outline</v-icon>
              {{ $t('traccar.device_info') }}
              <v-progress-circular v-if="loadingDetails" indeterminate size="16" width="2" class="ml-2"></v-progress-circular>
            </h3>
            <v-row>
              <v-col 
                v-for="item in deviceInfoItems" 
                :key="item.label" 
                cols="6" 
                sm="4" 
                md="3" 
                lg="2"
              >
                <v-list-item dense class="px-0">
                  <v-list-item-icon class="mr-2"><v-icon small :color="item.color || ''">{{ item.icon }}</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">{{ item.label }}</v-list-item-subtitle>
                    <v-list-item-title :class="item.textColor ? item.textColor + '--text' : ''">{{ item.value }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4" v-if="deviceData || loadingDetails"></v-divider>

        <!-- Скелетон для состояния -->
        <v-row v-if="loadingDetails && !positionData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-car-info</v-icon>
              {{ $t('traccar.device_status') }}
            </h3>
            <v-row>
              <v-col v-for="i in 6" :key="'skeleton-status-' + i" cols="6" sm="4" md="2" class="text-center">
                <v-skeleton-loader type="chip" class="mx-auto"></v-skeleton-loader>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Состояние устройства -->
        <v-row v-if="positionData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-car-info</v-icon>
              {{ $t('traccar.device_status') }}
            </h3>
            <v-row>
              <v-col cols="6" sm="4" md="2" class="text-center">
                <v-chip :color="deviceStatus === 'online' ? 'success' : (deviceStatus === 'offline' ? 'error' : 'grey')" dark small>
                  <v-icon left small>mdi-wifi</v-icon>
                  {{ $t('traccar.status.' + deviceStatus) }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionAttributes.ignition !== undefined">
                <v-chip :color="positionAttributes.ignition ? 'warning' : 'grey'" dark small>
                  <v-icon left small>mdi-engine</v-icon>
                  {{ positionAttributes.ignition ? $t('traccar.ignition_on') : $t('traccar.ignition_off') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionAttributes.motion !== undefined">
                <v-chip :color="positionAttributes.motion ? 'info' : 'grey'" dark small>
                  <v-icon left small>mdi-car-side</v-icon>
                  {{ positionAttributes.motion ? $t('traccar.moving') : $t('traccar.stationary') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionAttributes.blocked !== undefined">
                <v-chip :color="positionAttributes.blocked ? 'error' : 'success'" dark small>
                  <v-icon left small>mdi-lock</v-icon>
                  {{ positionAttributes.blocked ? $t('traccar.engine_blocked') : $t('traccar.engine_unblocked') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionAttributes.alarm">
                <v-chip color="error" dark small>
                  <v-icon left small>mdi-alarm-light</v-icon>
                  {{ $t('traccar.alarm') }}: {{ positionAttributes.alarm }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionAttributes.armed !== undefined">
                <v-chip :color="positionAttributes.armed ? 'success' : 'grey'" dark small>
                  <v-icon left small>mdi-shield-car</v-icon>
                  {{ positionAttributes.armed ? $t('traccar.armed') : $t('traccar.disarmed') }}
                </v-chip>
              </v-col>
            </v-row>
            
            <!-- Дополнительные показатели -->
            <v-row class="mt-2">
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionData.speed !== null">
                <v-chip outlined small>
                  <v-icon left small>mdi-speedometer</v-icon>
                  {{ positionData.speed }} {{ $t('custom.kmh') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionAttributes.battery != null">
                <v-chip :color="getBatteryColor(positionAttributes.battery)" dark small>
                  <v-icon left small>mdi-battery</v-icon>
                  {{ positionAttributes.battery }}%
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionAttributes.power != null">
                <v-chip outlined small>
                  <v-icon left small>mdi-car-battery</v-icon>
                  {{ positionAttributes.power.toFixed(1) }}V
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionAttributes.sat != null">
                <v-chip outlined small>
                  <v-icon left small>mdi-satellite-variant</v-icon>
                  {{ positionAttributes.sat }} {{ $t('traccar.satellites') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="positionAttributes.fuel != null">
                <v-chip outlined small>
                  <v-icon left small>mdi-gas-station</v-icon>
                  {{ positionAttributes.fuel }}%
                </v-chip>
              </v-col>
            </v-row>
            
            <!-- Последнее обновление -->
            <v-row class="mt-3" v-if="lastUpdate">
              <v-col cols="12">
                <span class="grey--text text--darken-1">
                  <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                  {{ $t('traccar.last_update') }}: {{ formatDateTime(lastUpdate) }}
                </span>
              </v-col>
            </v-row>

            <!-- Debug: Raw JSON -->
            <v-row class="mt-3">
              <v-col cols="12">
                <v-expansion-panels flat>
                  <v-expansion-panel>
                    <v-expansion-panel-header class="grey--text">
                      <v-icon small class="mr-2">mdi-code-json</v-icon>
                      Raw JSON
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <pre 
                        :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                        style="max-height: 400px; overflow: auto; padding: 10px; font-size: 11px; border-radius: 4px;"
                      >{{ JSON.stringify({ device: deviceData, position: positionData }, null, 2) }}</pre>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Геолокация -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">
              {{ $t('traccar.geolocation') }}
              <v-progress-circular v-if="loadingPosition" indeterminate size="16" width="2" class="ml-2"></v-progress-circular>
            </h3>
            <v-row align="center">
              <v-col cols="12" md="8">
                <v-skeleton-loader v-if="loadingPosition && !positionData" type="text" class="my-2"></v-skeleton-loader>
                
                <div v-else-if="positionData">
                  <p>
                    <strong>{{ $t('traccar.coordinates') }}:</strong> 
                    {{ positionData.latitude }}, {{ positionData.longitude }}
                    <span v-if="positionData.device_time" class="ml-2 grey--text text--darken-1">
                      ({{ $t('traccar.updated') }}: {{ formatDateTime(positionData.device_time) }})
                    </span>
                  </p>
                  <p v-if="positionData.address">
                    <strong>{{ $t('traccar.address') }}:</strong> {{ positionData.address }}
                  </p>
                </div>
                <div v-else>
                  <p class="grey--text">{{ $t('traccar.no_position_data') }}</p>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-right">
                <v-btn color="primary" small @click="openMap" :disabled="!positionData" class="mr-2">
                  <v-icon left small>mdi-map-marker</v-icon>
                  {{ $t('traccar.open_on_map') }}
                </v-btn>
                <v-btn color="secondary" small @click="refreshPosition" :loading="loadingPosition">
                  <v-icon left small>mdi-refresh</v-icon>
                  {{ $t('traccar.refresh') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Управление -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('traccar.controls') }}</h3>
            
            <v-row v-if="controlsLoaded && availableCommands.length === 0">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('traccar.no_commands_available') }}
                </v-alert>
              </v-col>
            </v-row>
            
            <v-row v-else align="center">
              <v-col cols="12" md="6" lg="5">
                <v-autocomplete
                  v-model="selectedCommand"
                  :items="availableCommands"
                  item-text="description"
                  item-value="type"
                  :label="$t('traccar.select_command')"
                  :placeholder="$t('traccar.select_command_placeholder')"
                  outlined
                  dense
                  clearable
                  :disabled="executingCommand !== null"
                  :loading="loadingControls"
                  return-object
                  @click="loadCommandTypes"
                >
                  <template v-slot:item="{ item }">
                    <span>{{ item.description || item.type }}</span>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span>{{ item.description || item.type }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="3" lg="2">
                <v-btn
                  color="primary"
                  :loading="executingCommand !== null"
                  :disabled="!selectedCommand || executingCommand !== null"
                  @click="sendCommand"
                  block
                  class="mb-6"
                >
                  <v-icon left>mdi-send</v-icon>
                  {{ $t('traccar.send_command') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Пробег -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('traccar.mileage') }}</h3>
            <v-row align="center">
              <v-col cols="12" md="8">
                <p>
                  <strong>{{ $t('traccar.system_mileage') }}:</strong> {{ car.mileage || 0 }} {{ $t('custom.km') }}
                </p>
                <p v-if="traccarMileage !== null">
                  <strong>{{ $t('traccar.traccar_mileage') }}:</strong> {{ traccarMileage }} {{ $t('custom.km') }}
                  <span v-if="mileageDifference !== null" 
                        :class="Math.abs(mileageDifference) > mileageThreshold ? 'red--text' : 'green--text'">
                    ({{ mileageDifference > 0 ? '+' : '' }}{{ mileageDifference }} {{ $t('custom.km') }})
                  </span>
                </p>
              </v-col>
              <v-col cols="12" md="4" class="d-flex flex-wrap justify-end" style="gap: 8px;">
                <v-btn color="info" small @click="fetchMileage" :loading="loadingMileage">
                  <v-icon left small>mdi-refresh</v-icon>
                  {{ $t('traccar.check_mileage') }}
                </v-btn>
                <v-btn
                  color="success"
                  small
                  @click="syncMileageFromTraccar"
                  :loading="syncingMileage"
                  :disabled="traccarMileage === null || Math.abs(mileageDifference) > mileageThreshold"
                >
                  <v-icon left small>mdi-download</v-icon>
                  {{ $t('traccar.sync_from_traccar') }}
                </v-btn>
                <v-btn
                  color="warning"
                  small
                  @click="syncMileageToTraccar"
                  :loading="syncingMileageToTraccar"
                >
                  <v-icon left small>mdi-upload</v-icon>
                  {{ $t('traccar.sync_to_traccar') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- История треков -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('traccar.track_history') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="trackStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(trackStartDate, trackStartTime)"
                      :label="$t('traccar.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="trackStartTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="trackStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="trackStartDate"
                          :max="today"
                          @input="trackStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="trackStartTime"
                          format="24hr"
                          @click:minute="trackStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="trackEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(trackEndDate, trackEndTime)"
                      :label="$t('traccar.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="trackEndTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="trackEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="trackEndDate"
                          :max="today"
                          @input="trackEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="trackEndTime"
                          format="24hr"
                          @click:minute="trackEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-btn color="primary" @click="fetchRoute" :loading="loadingRoute" block>
                  <v-icon left>mdi-map-marker-path</v-icon>
                  {{ $t('traccar.show_track') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <!-- Результат загрузки трека -->
            <v-row v-if="routeData" class="mt-4">
              <v-col cols="12">
                <v-alert type="success" dense outlined>
                  {{ $t('traccar.track_loaded') }}: {{ routeData.length }} {{ $t('traccar.points') }}
                  <v-btn small text color="primary" @click="showTrackOnMap" class="ml-2">
                    <v-icon left small>mdi-map</v-icon>
                    {{ $t('traccar.view_on_map') }}
                  </v-btn>
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Поездки -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('traccar.trips') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="tripsStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(tripsStartDate, tripsStartTime)"
                      :label="$t('traccar.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="tripsStartTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="tripsStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="tripsStartDate"
                          :max="today"
                          @input="tripsStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="tripsStartTime"
                          format="24hr"
                          @click:minute="tripsStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="tripsEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(tripsEndDate, tripsEndTime)"
                      :label="$t('traccar.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="tripsEndTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="tripsEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="tripsEndDate"
                          :max="today"
                          @input="tripsEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="tripsEndTime"
                          format="24hr"
                          @click:minute="tripsEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-btn color="primary" @click="fetchTrips" :loading="loadingTrips" block>
                  <v-icon left>mdi-car-multiple</v-icon>
                  {{ $t('traccar.load_trips') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <!-- Таблица поездок -->
            <v-row v-if="tripsData && tripsData.length > 0" class="mt-4">
              <v-col cols="12">
                <v-data-table
                  :headers="tripsHeaders"
                  :items="tripsData"
                  :items-per-page="5"
                  class="elevation-1"
                  dense
                >
                  <template v-slot:item.start_time="{ item }">
                    {{ formatDateTime(item.start_time) }}
                  </template>
                  <template v-slot:item.end_time="{ item }">
                    {{ formatDateTime(item.end_time) }}
                  </template>
                  <template v-slot:item.distance="{ item }">
                    {{ item.distance }} {{ $t('custom.km') }}
                  </template>
                  <template v-slot:item.average_speed="{ item }">
                    {{ item.average_speed }} {{ $t('custom.kmh') }}
                  </template>
                  <template v-slot:item.max_speed="{ item }">
                    {{ item.max_speed }} {{ $t('custom.kmh') }}
                  </template>
                  <template v-slot:item.duration_minutes="{ item }">
                    {{ formatDuration(item.duration_minutes) }}
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row v-else-if="tripsLoaded" class="mt-4">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('traccar.no_trips') }}
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Остановки -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('traccar.stops') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="stopsStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(stopsStartDate, stopsStartTime)"
                      :label="$t('traccar.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="stopsStartTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="stopsStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="stopsStartDate"
                          :max="today"
                          @input="stopsStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="stopsStartTime"
                          format="24hr"
                          @click:minute="stopsStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="stopsEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(stopsEndDate, stopsEndTime)"
                      :label="$t('traccar.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="stopsEndTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="stopsEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="stopsEndDate"
                          :max="today"
                          @input="stopsEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="stopsEndTime"
                          format="24hr"
                          @click:minute="stopsEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-btn color="primary" @click="fetchStops" :loading="loadingStops" block>
                  <v-icon left>mdi-map-marker-check</v-icon>
                  {{ $t('traccar.load_stops') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <!-- Таблица остановок -->
            <v-row v-if="stopsData && stopsData.length > 0" class="mt-4">
              <v-col cols="12">
                <v-data-table
                  :headers="stopsHeaders"
                  :items="stopsData"
                  :items-per-page="5"
                  class="elevation-1"
                  dense
                >
                  <template v-slot:item.start_time="{ item }">
                    {{ formatDateTime(item.start_time) }}
                  </template>
                  <template v-slot:item.end_time="{ item }">
                    {{ formatDateTime(item.end_time) }}
                  </template>
                  <template v-slot:item.duration_minutes="{ item }">
                    {{ formatDuration(item.duration_minutes) }}
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row v-else-if="stopsLoaded" class="mt-4">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('traccar.no_stops') }}
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Сводка -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('traccar.summary') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="summaryStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(summaryStartDate, summaryStartTime)"
                      :label="$t('traccar.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="summaryStartTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="summaryStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="summaryStartDate"
                          :max="today"
                          @input="summaryStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="summaryStartTime"
                          format="24hr"
                          @click:minute="summaryStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="summaryEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(summaryEndDate, summaryEndTime)"
                      :label="$t('traccar.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="summaryEndTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="summaryEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="summaryEndDate"
                          :max="today"
                          @input="summaryEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="summaryEndTime"
                          format="24hr"
                          @click:minute="summaryEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-btn color="primary" @click="fetchSummary" :loading="loadingSummary" block>
                  <v-icon left>mdi-chart-box</v-icon>
                  {{ $t('traccar.load_summary') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <!-- Результат сводки -->
            <v-row v-if="summaryData" class="mt-4">
              <v-col cols="12" sm="6" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <div class="text-h6">{{ summaryData.distance }} {{ $t('custom.km') }}</div>
                    <div class="text-caption grey--text">{{ $t('traccar.total_distance') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <div class="text-h6">{{ summaryData.average_speed }} {{ $t('custom.kmh') }}</div>
                    <div class="text-caption grey--text">{{ $t('traccar.average_speed') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <div class="text-h6">{{ summaryData.max_speed }} {{ $t('custom.kmh') }}</div>
                    <div class="text-caption grey--text">{{ $t('traccar.max_speed') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined>
                  <v-card-text class="text-center">
                    <div class="text-h6">{{ summaryData.engine_hours_formatted || '0:00' }}</div>
                    <div class="text-caption grey--text">{{ $t('traccar.engine_hours') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- События -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('traccar.events_title') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="eventsStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(eventsStartDate, eventsStartTime)"
                      :label="$t('traccar.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="eventsStartTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="eventsStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="eventsStartDate"
                          :max="today"
                          @input="eventsStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="eventsStartTime"
                          format="24hr"
                          @click:minute="eventsStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="eventsEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(eventsEndDate, eventsEndTime)"
                      :label="$t('traccar.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="eventsEndTab" fixed-tabs>
                      <v-tab>{{ $t('traccar.date') }}</v-tab>
                      <v-tab>{{ $t('traccar.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="eventsEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="eventsEndDate"
                          :max="today"
                          @input="eventsEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="eventsEndTime"
                          format="24hr"
                          @click:minute="eventsEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-btn color="primary" @click="fetchEvents" :loading="loadingEvents" block>
                  <v-icon left>mdi-history</v-icon>
                  {{ $t('traccar.load_events') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <!-- Таблица событий -->
            <v-row v-if="eventsData && eventsData.length > 0" class="mt-4">
              <v-col cols="12">
                <v-data-table
                  :headers="eventsHeaders"
                  :items="eventsData"
                  :items-per-page="10"
                  class="elevation-1"
                  dense
                >
                  <template v-slot:item.server_time="{ item }">
                    {{ formatDateTime(item.server_time) }}
                  </template>
                  <template v-slot:item.type="{ item }">
                    <v-chip :color="getEventColor(item.type)" dark x-small>
                      {{ $t(item.type_localized) || item.type }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row v-else-if="eventsLoaded" class="mt-4">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('traccar.no_events') }}
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Геозоны -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">
              {{ $t('traccar.geofences') }}
              <v-btn icon small @click="fetchGeofences" :loading="loadingGeofences" class="ml-2">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </h3>
            
            <v-row v-if="geofencesData && geofencesData.length > 0">
              <v-col cols="12">
                <v-data-table
                  :headers="geofencesHeaders"
                  :items="geofencesData"
                  :items-per-page="5"
                  class="elevation-1"
                  dense
                >
                  <template v-slot:item.area_type="{ item }">
                    <div class="text-center">
                      <v-chip :color="getGeofenceTypeColor(item.area_type)" dark x-small>
                        {{ item.area_type }}
                      </v-chip>
                    </div>
                  </template>
                  <template v-slot:item.is_linked="{ item }">
                    <div class="text-center">
                      <v-icon v-if="item.is_linked" color="success">mdi-check-circle</v-icon>
                      <v-icon v-else color="grey lighten-1">mdi-circle-outline</v-icon>
                    </div>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <div class="text-center">
                      <v-btn 
                        v-if="!item.is_linked" 
                        icon 
                        small
                        @click="linkGeofence(item.id)" 
                        :loading="linkingGeofence === item.id"
                        color="success"
                        :title="$t('traccar.link_geofence')"
                      >
                        <v-icon>mdi-link-plus</v-icon>
                      </v-btn>
                      <v-btn 
                        v-else
                        icon 
                        small
                        @click="unlinkGeofence(item.id)" 
                        :loading="unlinkingGeofence === item.id"
                        color="error"
                        :title="$t('traccar.unlink_geofence')"
                      >
                        <v-icon>mdi-link-off</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row v-else-if="geofencesLoaded">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('traccar.no_geofences') }}
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

      </v-container>
    </v-card-text>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          {{ $t('custom.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Track Map Dialog -->
    <v-dialog v-model="trackMapDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title>
          {{ $t('traccar.track_map') }}
          <v-spacer></v-spacer>
          <v-btn icon @click="trackMapDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text style="height: 500px; padding: 0;">
          <GmapMap
            ref="trackMap"
            :center="trackCenter"
            :zoom="trackZoom"
            map-type-id="roadmap"
            style="width: 100%; height: 100%"
          >
            <GmapPolyline
              v-if="trackPath.length > 0"
              :path="trackPath"
              :options="{ strokeColor: '#1976D2', strokeWeight: 3 }"
            />
            <GmapMarker
              v-if="trackPath.length > 0"
              :position="trackPath[0]"
              :icon="{ url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png' }"
            />
            <GmapMarker
              v-if="trackPath.length > 1"
              :position="trackPath[trackPath.length - 1]"
              :icon="{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }"
            />
          </GmapMap>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'TraccarTab',
  props: {
    car: {
      type: Object,
      required: true
    },
    company: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Device details
      deviceData: null,
      positionData: null,
      loadingDetails: true,
      loadingPosition: true,
      
      // Commands
      availableCommands: [],
      selectedCommand: null,
      executingCommand: null,
      loadingControls: false,
      controlsLoaded: false,
      
      // Mileage
      traccarMileage: null,
      mileageDifference: null,
      loadingMileage: false,
      syncingMileage: false,
      syncingMileageToTraccar: false,
      
      // Track history
      trackStartDate: this.getYesterday(),
      trackStartTime: '00:00',
      trackEndDate: this.getToday(),
      trackEndTime: '23:59',
      trackStartMenu: false,
      trackEndMenu: false,
      trackStartTab: 0,
      trackEndTab: 0,
      routeData: null,
      loadingRoute: false,
      
      // Track map dialog
      trackMapDialog: false,
      trackPath: [],
      trackCenter: { lat: 55.7558, lng: 37.6173 },
      trackZoom: 12,
      
      // Trips
      tripsStartDate: this.getYesterday(),
      tripsStartTime: '00:00',
      tripsEndDate: this.getToday(),
      tripsEndTime: '23:59',
      tripsStartMenu: false,
      tripsEndMenu: false,
      tripsStartTab: 0,
      tripsEndTab: 0,
      tripsData: null,
      tripsLoaded: false,
      loadingTrips: false,
      
      // Stops
      stopsStartDate: this.getYesterday(),
      stopsStartTime: '00:00',
      stopsEndDate: this.getToday(),
      stopsEndTime: '23:59',
      stopsStartMenu: false,
      stopsEndMenu: false,
      stopsStartTab: 0,
      stopsEndTab: 0,
      stopsData: null,
      stopsLoaded: false,
      loadingStops: false,
      
      // Summary
      summaryStartDate: this.getYesterday(),
      summaryStartTime: '00:00',
      summaryEndDate: this.getToday(),
      summaryEndTime: '23:59',
      summaryStartMenu: false,
      summaryEndMenu: false,
      summaryStartTab: 0,
      summaryEndTab: 0,
      summaryData: null,
      loadingSummary: false,
      
      // Events
      eventsStartDate: this.getYesterday(),
      eventsStartTime: '00:00',
      eventsEndDate: this.getToday(),
      eventsEndTime: '23:59',
      eventsStartMenu: false,
      eventsEndMenu: false,
      eventsStartTab: 0,
      eventsEndTab: 0,
      eventsData: null,
      eventsLoaded: false,
      loadingEvents: false,
      
      // Geofences
      geofencesData: null,
      geofencesLoaded: false,
      loadingGeofences: false,
      linkingGeofence: null,
      unlinkingGeofence: null,
      
      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success'
    };
  },
  computed: {
    today() {
      return new Date().toISOString().substr(0, 10);
    },
    mileageThreshold() {
      return 500;
    },
    deviceStatus() {
      return this.deviceData?.status || 'unknown';
    },
    lastUpdate() {
      return this.deviceData?.last_update || this.positionData?.server_time;
    },
    positionAttributes() {
      return this.positionData?.attributes || {};
    },
    deviceInfoItems() {
      const items = [];
      const d = this.deviceData;
      const p = this.positionData;
      if (!d) return items;
      
      if (d.name) {
        items.push({ icon: 'mdi-tag', label: this.$t('traccar.device_name'), value: d.name });
      }
      if (d.unique_id) {
        items.push({ icon: 'mdi-identifier', label: this.$t('traccar.unique_id'), value: d.unique_id });
      }
      if (d.model) {
        items.push({ icon: 'mdi-devices', label: this.$t('traccar.model'), value: d.model });
      }
      if (d.phone) {
        items.push({ icon: 'mdi-phone', label: this.$t('traccar.phone'), value: d.phone });
      }
      if (d.category) {
        items.push({ icon: 'mdi-shape', label: this.$t('traccar.category'), value: d.category });
      }
      if (p?.protocol) {
        items.push({ icon: 'mdi-protocol', label: this.$t('traccar.protocol'), value: p.protocol });
      }
      
      return items;
    },
    tripsHeaders() {
      return [
        { text: this.$t('traccar.start_time'), value: 'start_time' },
        { text: this.$t('traccar.end_time'), value: 'end_time' },
        { text: this.$t('traccar.start_address'), value: 'start_address' },
        { text: this.$t('traccar.end_address'), value: 'end_address' },
        { text: this.$t('traccar.distance'), value: 'distance' },
        { text: this.$t('traccar.avg_speed'), value: 'average_speed' },
        { text: this.$t('traccar.max_speed'), value: 'max_speed' },
        { text: this.$t('traccar.duration'), value: 'duration_minutes' }
      ];
    },
    stopsHeaders() {
      return [
        { text: this.$t('traccar.start_time'), value: 'start_time' },
        { text: this.$t('traccar.end_time'), value: 'end_time' },
        { text: this.$t('traccar.address'), value: 'address' },
        { text: this.$t('traccar.duration'), value: 'duration_minutes' }
      ];
    },
    eventsHeaders() {
      return [
        { text: this.$t('traccar.time'), value: 'server_time' },
        { text: this.$t('traccar.event_type'), value: 'type' }
      ];
    },
    geofencesHeaders() {
      return [
        { text: this.$t('traccar.name'), value: 'name' },
        { text: this.$t('traccar.type'), value: 'area_type', align: 'center' },
        { text: this.$t('traccar.linked'), value: 'is_linked', sortable: true, align: 'center' },
        { text: this.$t('traccar.description'), value: 'description', align: 'center' },
        { text: this.$t('traccar.actions'), value: 'actions', sortable: false, align: 'center' }
      ];
    }
  },
  mounted() {
    this.fetchDeviceDetails();
    this.fetchPosition();
    this.fetchGeofences();
  },
  methods: {
    // Date helpers
    getToday() {
      return new Date().toISOString().substr(0, 10);
    },
    getYesterday() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toISOString().substr(0, 10);
    },
    
    // Formatting
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString();
    },
    formatDateTimeInput(date, time) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}.${month}.${year} ${time || '00:00'}`;
    },
    formatDuration(minutes) {
      if (!minutes) return '0м';
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      if (hours > 0) {
        return `${hours}ч ${mins}м`;
      }
      return `${mins}м`;
    },
    
    // API headers
    getAuthHeaders() {
      return {
        headers: {
          Authorization: this.$store.getters.getAuthToken,
        },
      };
    },
    
    // Date to timestamp conversion
    dateTimeToTimestamp(date, time) {
      const [year, month, day] = date.split('-');
      const [hours, minutes] = time.split(':');
      return new Date(year, month - 1, day, hours, minutes).getTime() / 1000;
    },
    
    // API calls
    async fetchDeviceDetails() {
      this.loadingDetails = true;
      try {
        const response = await this.axios.get(`/api/v1/traccar/${this.car.id}/details`, this.getAuthHeaders());
        this.deviceData = response.data.device;
        this.positionData = response.data.position;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingDetails = false;
      }
    },
    
    async fetchPosition() {
      this.loadingPosition = true;
      try {
        const response = await this.axios.get(`/api/v1/traccar/${this.car.id}/position`, this.getAuthHeaders());
        this.positionData = response.data;
      } catch (error) {
        // Позиция может быть недоступна, не показываем ошибку
        console.error('Failed to fetch position:', error);
      } finally {
        this.loadingPosition = false;
      }
    },
    
    async refreshPosition() {
      await this.fetchPosition();
      this.showSuccess(this.$t('traccar.position_refreshed'));
    },
    
    openMap() {
      if (this.positionData) {
        const lat = this.positionData.latitude;
        const lon = this.positionData.longitude;
        window.open(`https://www.google.com/maps?q=${lat},${lon}`, '_blank');
      }
    },
    
    async loadCommandTypes() {
      if (this.controlsLoaded) return;
      
      this.loadingControls = true;
      try {
        const response = await this.axios.get(`/api/v1/traccar/${this.car.id}/command_types`, this.getAuthHeaders());
        this.availableCommands = response.data.command_types || [];
        this.controlsLoaded = true;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingControls = false;
      }
    },
    
    async sendCommand() {
      if (!this.selectedCommand) return;
      
      this.executingCommand = this.selectedCommand.type;
      try {
        await this.axios.post(`/api/v1/traccar/${this.car.id}/command`, {
          type: this.selectedCommand.type
        }, this.getAuthHeaders());
        this.showSuccess(this.$t('traccar.command_sent'));
        this.selectedCommand = null;
      } catch (error) {
        this.showError(error);
      } finally {
        this.executingCommand = null;
      }
    },
    
    async fetchMileage() {
      this.loadingMileage = true;
      try {
        const response = await this.axios.get(`/api/v1/traccar/${this.car.id}/mileage`, this.getAuthHeaders());
        this.traccarMileage = response.data.traccar_mileage;
        this.mileageDifference = response.data.difference;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingMileage = false;
      }
    },
    
    async syncMileageFromTraccar() {
      this.syncingMileage = true;
      try {
        await this.axios.post(`/api/v1/traccar/${this.car.id}/mileage/sync`, {
          direction: 'from_traccar'
        }, this.getAuthHeaders());
        this.showSuccess(this.$t('traccar.mileage_synced'));
        this.$emit('mileage-updated');
      } catch (error) {
        this.showError(error);
      } finally {
        this.syncingMileage = false;
      }
    },
    
    async syncMileageToTraccar() {
      this.syncingMileageToTraccar = true;
      try {
        await this.axios.post(`/api/v1/traccar/${this.car.id}/mileage/sync`, {
          direction: 'to_traccar'
        }, this.getAuthHeaders());
        this.showSuccess(this.$t('traccar.mileage_synced_to_traccar'));
      } catch (error) {
        this.showError(error);
      } finally {
        this.syncingMileageToTraccar = false;
      }
    },
    
    async fetchRoute() {
      this.loadingRoute = true;
      try {
        const from = this.dateTimeToTimestamp(this.trackStartDate, this.trackStartTime);
        const to = this.dateTimeToTimestamp(this.trackEndDate, this.trackEndTime);
        
        const response = await this.axios.get(`/api/v1/traccar/${this.car.id}/route`, {
          ...this.getAuthHeaders(),
          params: { from, to }
        });
        this.routeData = response.data.route || [];
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingRoute = false;
      }
    },
    
    showTrackOnMap() {
      if (!this.routeData || this.routeData.length === 0) return;
      
      this.trackPath = this.routeData.map(p => ({
        lat: p.latitude,
        lng: p.longitude
      }));
      
      // Центрируем карту на первой точке
      if (this.trackPath.length > 0) {
        this.trackCenter = this.trackPath[0];
      }
      
      this.trackMapDialog = true;
    },
    
    async fetchTrips() {
      this.loadingTrips = true;
      try {
        const from = this.dateTimeToTimestamp(this.tripsStartDate, this.tripsStartTime);
        const to = this.dateTimeToTimestamp(this.tripsEndDate, this.tripsEndTime);
        
        const response = await this.axios.get(`/api/v1/traccar/${this.car.id}/trips`, {
          ...this.getAuthHeaders(),
          params: { from, to }
        });
        this.tripsData = response.data.trips || [];
        this.tripsLoaded = true;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingTrips = false;
      }
    },
    
    async fetchStops() {
      this.loadingStops = true;
      try {
        const from = this.dateTimeToTimestamp(this.stopsStartDate, this.stopsStartTime);
        const to = this.dateTimeToTimestamp(this.stopsEndDate, this.stopsEndTime);
        
        const response = await this.axios.get(`/api/v1/traccar/${this.car.id}/stops`, {
          ...this.getAuthHeaders(),
          params: { from, to }
        });
        this.stopsData = response.data.stops || [];
        this.stopsLoaded = true;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingStops = false;
      }
    },
    
    async fetchSummary() {
      this.loadingSummary = true;
      try {
        const from = this.dateTimeToTimestamp(this.summaryStartDate, this.summaryStartTime);
        const to = this.dateTimeToTimestamp(this.summaryEndDate, this.summaryEndTime);
        
        const response = await this.axios.get(`/api/v1/traccar/${this.car.id}/summary`, {
          ...this.getAuthHeaders(),
          params: { from, to }
        });
        this.summaryData = response.data.summary;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingSummary = false;
      }
    },
    
    async fetchEvents() {
      this.loadingEvents = true;
      try {
        const from = this.dateTimeToTimestamp(this.eventsStartDate, this.eventsStartTime);
        const to = this.dateTimeToTimestamp(this.eventsEndDate, this.eventsEndTime);
        
        const response = await this.axios.get(`/api/v1/traccar/${this.car.id}/events`, {
          ...this.getAuthHeaders(),
          params: { from, to }
        });
        this.eventsData = response.data.events || [];
        this.eventsLoaded = true;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingEvents = false;
      }
    },
    
    async fetchGeofences() {
      this.loadingGeofences = true;
      try {
        const response = await this.axios.get('/api/v1/traccar/geofences', {
          ...this.getAuthHeaders(),
          params: { car_id: this.car.id }
        });
        this.geofencesData = response.data.geofences || [];
        this.geofencesLoaded = true;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingGeofences = false;
      }
    },
    
    async linkGeofence(geofenceId) {
      this.linkingGeofence = geofenceId;
      try {
        await this.axios.post(`/api/v1/traccar/geofences/${geofenceId}/link/${this.car.id}`, {}, this.getAuthHeaders());
        this.showSuccess(this.$t('traccar.geofence_linked'));
        // Обновляем список геозон
        await this.fetchGeofences();
      } catch (error) {
        this.showError(error);
      } finally {
        this.linkingGeofence = null;
      }
    },
    
    async unlinkGeofence(geofenceId) {
      this.unlinkingGeofence = geofenceId;
      try {
        await this.axios.delete(`/api/v1/traccar/geofences/${geofenceId}/unlink/${this.car.id}`, this.getAuthHeaders());
        this.showSuccess(this.$t('traccar.geofence_unlinked'));
        // Обновляем список геозон
        await this.fetchGeofences();
      } catch (error) {
        this.showError(error);
      } finally {
        this.unlinkingGeofence = null;
      }
    },
    
    // UI helpers
    getBatteryColor(level) {
      if (level > 70) return 'success';
      if (level > 30) return 'warning';
      return 'error';
    },
    
    getEventColor(type) {
      const colors = {
        deviceOnline: 'success',
        deviceOffline: 'error',
        deviceMoving: 'info',
        deviceStopped: 'grey',
        deviceOverspeed: 'warning',
        alarm: 'error',
        ignitionOn: 'warning',
        ignitionOff: 'grey',
        geofenceEnter: 'info',
        geofenceExit: 'warning'
      };
      return colors[type] || 'grey';
    },
    
    getGeofenceTypeColor(type) {
      const colors = {
        circle: 'info',
        polygon: 'success',
        linestring: 'warning'
      };
      return colors[type] || 'grey';
    },
    
    showSuccess(message) {
      this.snackbarText = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    
    showError(error) {
      const message = error.response?.data?.error || error.message || this.$t('traccar.error');
      this.snackbarText = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    }
  }
};
</script>
